import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import { DefaultRouteAction, DefaultRouteParam } from "constants/routes";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FormMode, formUtils } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import styles from "./group-panel.module.scss";
import { Formatters } from "helper/formatters";
import {
  IAMEntityNameSection,
  IIAMEntityNameSectionErrors,
} from "../../shared-ui/iam/sections/entity-name/entity-name-section";
import { iamService } from "api/services/iam";
import DetailsPanelFooter from "components/shared-ui/details-panel-footer/details-panel-footer";
import {
  IIAMGroup,
  IIAMGroupPolicyRequest,
  IIAMGroupRequest,
  IIAMGroupUpdateRequest,
} from "api/entities/iam-groups";
import { IAMEntity } from "utils/validators";
import PoliciesSection from "components/iam-users/user-panel/sections/policies/policies-section";
import { IIAMAttachedPolicy } from "api/entities/iam-policies";
import { areArraysEqual } from "utils/array-utils";
import IAMPolicyPanel from "components/iam-policies/policy-panel/policy-panel";
import { Label } from "components/shared-ui/label";

export interface IIAMGroupPanelProps {
  fetchGroups?: () => void;
  mode?: FormMode;
  embedded: boolean;
  onClose: () => void;
}

export default function IAMGroupPanel(props: IIAMGroupPanelProps) {
  const { idOrAction } = useParams<{
    [DefaultRouteParam.IdOrAction]: string;
  }>();

  const [api, isLoading] = useApi();

  const open = props.embedded ? props.mode === FormMode.Create : !!idOrAction;
  const isGroupCreation = props.embedded
    ? props.mode === FormMode.Create
    : idOrAction === DefaultRouteAction.New;

  const groupName = isGroupCreation ? null : idOrAction;

  const [group, setGroup] = useState<IIAMGroup>(null);
  const [errors, setErrors] = useState<IIAMEntityNameSectionErrors>(null);
  const [groupCreateRequest, setGroupCreateRequest] =
    useState<IIAMGroupRequest>({ GroupName: "" });
  const [attachedPolicies, setAttachedPolicies] = useState<
    IIAMAttachedPolicy[]
  >([]);
  const [selectedPolicies, setSelectedPolicies] = useState<
    IIAMAttachedPolicy[]
  >([]);
  const [policyPanelFormMode, setPolicyPanelFormMode] =
    useState<FormMode>(null);

  const canProceed = () => {
    return (
      (!!errors &&
        !Object.keys(errors).some((key) => errors[key]) &&
        !!groupCreateRequest.GroupName &&
        groupCreateRequest.GroupName != groupName) ||
      !areArraysEqual(attachedPolicies, selectedPolicies)
    );
  };

  const handleError = (errors: IIAMEntityNameSectionErrors) => {
    setErrors((prevErrors) => ({ ...prevErrors, ...errors }));
  };

  const handleChange = (groupUpdate: Partial<IIAMGroupRequest>) => {
    setGroupCreateRequest((prev) => ({ ...prev, ...groupUpdate }));
  };

  const handleAddPolicyFormPanelOpen = () => {
    setPolicyPanelFormMode(FormMode.Create);
  };
  const handleAddPolicyFormPanelClose = () => {
    setPolicyPanelFormMode(null);
  };
  const handlePolicyChange = (groups: IIAMAttachedPolicy[]) => {
    setSelectedPolicies(groups);
  };

  const attachPolicy = async (req: IIAMGroupPolicyRequest) => {
    await api(iamService.attachPolicy(req));
  };
  const detachPolicy = async (req: IIAMGroupPolicyRequest) => {
    await api(iamService.detachPolicy(req));
  };

  const updatePolicies = async (GroupName: string) => {
    if (areArraysEqual(attachedPolicies, selectedPolicies)) return;
    const currentCount = attachedPolicies.length;
    const toRemove =
      currentCount > 0
        ? attachedPolicies.filter(
            (current) => !selectedPolicies.includes(current),
          )
        : [];
    const toAdd =
      currentCount > 0
        ? selectedPolicies.filter(
            (selected) => !attachedPolicies.includes(selected),
          )
        : selectedPolicies;

    toRemove.forEach(({ PolicyArn }) => detachPolicy({ GroupName, PolicyArn }));
    toAdd.forEach(({ PolicyArn }) => attachPolicy({ GroupName, PolicyArn }));
  };

  const save = async () => {
    const request = formUtils.trimStringValues(groupCreateRequest);
    if (isGroupCreation) {
      const group = await api(iamService.createGroup(request));
      await updatePolicies(group.GroupName);

      props.fetchGroups?.();
      props.onClose();
      return group;
    } else {
      const updatedGroup = await api(
        iamService.updateGroup({
          GroupName: group.GroupName,
          NewGroupName: request.GroupName,
        }),
      );

      await updatePolicies(updatedGroup.GroupName);
      props.fetchGroups?.();
      props.onClose();
      return updatedGroup;
    }
  };

  useEffect(() => {
    if (open) {
      if (isGroupCreation) {
        setGroupCreateRequest({ GroupName: "" });
        setGroup(null);
      } else {
        api(iamService.getGroup(groupName)).then((group) => {
          setGroupCreateRequest({
            GroupName: group.GroupName,
          });
          setGroup(group);
          api(
            iamService.listAttachedPolicies({ GroupName: group.GroupName }),
          ).then((page) => {
            setAttachedPolicies(page.data);
            setSelectedPolicies(page.data);
          });
        });
      }

      setErrors(null);
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <>
      <DetailsPanel
        open={open}
        isLoading={isLoading}
        onCloseClick={props.onClose}
        title={isGroupCreation ? "Add New Group" : "Group Details"}
        className={styles.panel}
      >
        {open && (
          <div className={styles["group-info"]}>
            {!!group && (
              <>
                <section>
                  <Label label="id" value={group.GroupId} />
                  <Label label="resource arn" value={group.Arn} />
                  <Label
                    label="created at"
                    value={Formatters.formatDateTime(group.CreateDate)}
                  />
                </section>
              </>
            )}

            {isGroupCreation && (
              <div className={styles.description}>
                A Group is a collection of users within Myota used to simplify
                and centralize permissions management. It does not have
                credentials or access rights of its own, but instead acts as a
                container for policies. By assigning policies to a group, all
                users in that group automatically inherit the permissions,
                ensuring consistent access control. Groups allow administrators
                to manage large sets of users efficiently, enforce the principle
                of least privilege, and streamline updates by modifying
                permissions in one place rather than on each individual user.
              </div>
            )}

            <IAMEntityNameSection<IIAMGroupRequest>
              className={styles.name}
              request={groupCreateRequest}
              field="GroupName"
              type={IAMEntity.Group}
              onChange={handleChange}
              onError={handleError}
              errors={errors}
            />

            <PoliciesSection
              policies={attachedPolicies}
              isCreate={isGroupCreation}
              onChange={handlePolicyChange}
              onNewPolicy={handleAddPolicyFormPanelOpen}
            />

            <DetailsPanelFooter
              sticky={true}
              saveButtonText={"Save"}
              saveButtonDisabled={!canProceed()}
              onSaveButtonClick={save}
              onCancelButtonClick={props.onClose}
            />
          </div>
        )}
      </DetailsPanel>
      <IAMPolicyPanel
        embedded
        mode={policyPanelFormMode}
        onClose={handleAddPolicyFormPanelClose}
      />
    </>
  );
}
